/**
 * The demo day with runtime facts: the Live-card states (chapter 7) and the
 * "you always win" stages (chapter 6) — pinned, skipped, re-synced, reset —
 * laid over WARM UP's drops exactly as the engine would annotate them.
 * For Media Devoted | Created By Ciro Autiero
 */
import type {
  DayPlanContainerRow,
  DayPlanDropRow,
  DayPlanPreview,
  DayPlanSkippedList,
  DropGenDropRuntime,
} from '@trafficpulse/shared';

import { CLICKERS_LISTS, DEMO_DAY, atUtc } from '../demo-day';

export type RuntimeVariant = 'none' | 'waiting' | 'running' | 'done';

const runtime = (over: Partial<DropGenDropRuntime> = {}): DropGenDropRuntime => ({
  tombstoned: false, pinned: false, hasDraft: true, skipReason: null, sentAt: null, staged: false,
  ...over,
});

const runtimeFor = (variant: RuntimeVariant, i: number, total: number): DropGenDropRuntime | null => {
  if (variant === 'none') return null;
  const sent = variant === 'done' || (variant === 'running' && i < Math.ceil(total / 3));
  return runtime({ sentAt: sent ? atUtc(11 * 60 + i * 35) : null });
};

/** The same day with runtime facts, for the Live-card chapter. */
export function withRuntime(variant: RuntimeVariant): DayPlanPreview {
  return {
    ...DEMO_DAY,
    containers: DEMO_DAY.containers.map((c) => ({
      ...c,
      drops: c.drops.map((d, i) => ({ ...d, runtime: runtimeFor(variant, i, c.drops.length) })),
    })),
  };
}

export type HumanWinsStage = 'generated' | 'pinned' | 'skipped' | 'resynced' | 'reset';
export const HUMAN_WINS_STAGES: Array<{ stage: HumanWinsStage; label: string }> = [
  { stage: 'generated', label: 'Generate lays out the drafts' },
  { stage: 'pinned', label: 'you edit one by hand — pinned' },
  { stage: 'skipped', label: 'you delete one — skipped today' },
  { stage: 'resynced', label: 'Generate again — both survive' },
  { stage: 'reset', label: 'Reset hands the slot back' },
];
const PINNED_AT = 2;
const SKIPPED_AT = 4;

/** WARM UP's drops at one stage of the chapter-6 story. */
export function warmUpAt(stage: HumanWinsStage): DayPlanDropRow[] {
  const order = HUMAN_WINS_STAGES.findIndex((s) => s.stage === stage);
  const pinned = order >= 1;
  const skipped = order >= 2 && stage !== 'reset';
  return DEMO_DAY.containers[0].drops.map((d, i) => ({
    ...d,
    runtime: runtime({
      pinned: pinned && i === PINNED_AT,
      tombstoned: skipped && i === SKIPPED_AT,
      hasDraft: !(skipped && i === SKIPPED_AT),
    }),
  }));
}

const DAY_MS = 86_400_000;
const STOPPED = 'stopped — list failed';
const burned = (i: number, reason: DayPlanSkippedList['reason'], daysAgo: number): DayPlanSkippedList => ({
  wiserId: CLICKERS_LISTS[i].wiserId,
  name: CLICKERS_LISTS[i].name,
  reason,
  burnedAt: new Date(Date.now() - daysAgo * DAY_MS).toISOString(),
});
const listRef = (i: number) => ({ wiserId: CLICKERS_LISTS[i].wiserId, name: CLICKERS_LISTS[i].name });

/** Chapter 8: CLEARANCE-CLICKS on day D1..D6 of the verdict story. */
export function clearanceOnDay(day: number): DayPlanContainerRow {
  const c = DEMO_DAY.containers[1];
  const g = c.groups[0];
  const sentUpTo = day <= 2 ? c.drops.length : day === 3 ? Math.floor(c.drops.length / 3) : 0;
  const drops = c.drops.map((d, i) => ({
    ...d,
    runtime: day >= 6 ? null : runtime({
      sentAt: i < sentUpTo ? atUtc(d.minute) : null,
      tombstoned: day === 3 && i >= sentUpTo,
      hasDraft: !(day === 3 && i >= sentUpTo),
      skipReason: day === 3 && i >= sentUpTo ? STOPPED : null,
    }),
  }));
  const skipped =
    day <= 2 ? [] : day === 3 ? [burned(1, 'terrible', 0)] : day === 4 ? [burned(1, 'terrible', 1)]
    : day === 5 ? [burned(1, 'terrible', 2), burned(0, 'depleted', 0)]
    : CLICKERS_LISTS.map((_, i) => burned(i, i % 2 ? 'terrible' : 'depleted', 0));
  const today = day <= 3 ? listRef(1) : day === 4 ? listRef(0) : day === 5 ? listRef(2) : null;
  const next = day <= 3 ? listRef(0) : day === 4 ? listRef(2) : day === 5 ? listRef(3) : null;
  const paused = day >= 6 ? { reason: 'pool_exhausted', burned: CLICKERS_LISTS.length } : null;
  return { ...c, drops, groups: [{ ...g, listOfDay: today, next, skipped, paused }] };
}
